import React, { useEffect, useRef, useState } from "react";
import $ from "jquery";
import "datatables.net";
import "datatables.net-responsive";
import { Link, useNavigate } from "react-router-dom";
import { useGlobalFunction } from "../../../Context/GlobalFunctionContext";
import { useAuth } from "../../../Context/AuthContext";
import { buildKey, readPage, writePage, buildViewSignature, saveViewState, readViewState } from "./orderHistoryCache";
import "../../../assets/css/Spinner.css";
import "./orderHistoryTable.css";

const SORT_COLUMN_BY_INDEX = {
  0: "custOrderNo",
  1: "invoiceNo",
  2: "orderId",
  3: "createdDate",
  4: "orderDate",
};
const DEFAULT_SORT_COLUMN = "createdDate";

const statusClassMap = {
  "open-order": [1],
  "delete-order": [5, 12], // 👈 both 2 and 4 map here
  "payment-required": [15, 22, 18],
  "processing-order": [14, 19, 6],
  "ready-pickup": [9],
};
const paymentRequiredStatusIds = [18];
const invoiceEligible = JSON.parse(process.env.REACT_APP_INVOICE_ELIGIBLE_STATUS || "[]");

export const OrderHistoryTable = ({ filters, handleShow }) => {
  const tableRef = useRef(null);
  const dtRef = useRef(null);
  const navigate = useNavigate();
  const { sendInvoiceSignal, getOrderHistory } = useGlobalFunction();
  const { userId } = useAuth();

  const filtersRef = useRef(filters);
  const getOrderHistoryRef = useRef(getOrderHistory);
  const userIdRef = useRef(userId);
  useEffect(() => {
    getOrderHistoryRef.current = getOrderHistory;
    userIdRef.current = userId;
  });

  const isFirstFilters = useRef(true);
  useEffect(() => {
    filtersRef.current = filters;
    if (isFirstFilters.current) {
      isFirstFilters.current = false;
      return;
    }
    if (dtRef.current) dtRef.current.ajax.reload(null, true);
  }, [filters]);

  useEffect(() => {
    const $table = $(tableRef.current);
    function markFirstVisible() {
      $table.find("tbody tr").each(function () {
        const $cells = $(this).children("td").removeClass("orderCustomClass");
        const $firstVisible = $cells
          .filter(function () {
            const $td = $(this);
            return $td.is(":visible") && !$td.hasClass("dtr-hidden");
          })
          .first();
        $firstVisible.addClass("orderCustomClass");
      });
    }
    const viewSignature = () => buildViewSignature({ ...filtersRef.current, userId: userIdRef.current });

    dtRef.current = $table.DataTable({
      serverSide: true,
      processing: true,
      stateSave: true,
      stateSaveCallback: (settings, data) => saveViewState(viewSignature(), data),
      stateLoadCallback: () => readViewState(viewSignature()),
      ajax: (params, callback) => {
        const pageNo = Math.floor(params.start / params.length) + 1;
        const ord = params.order && params.order[0];
        const sortColumn = (ord && SORT_COLUMN_BY_INDEX[ord.column]) || DEFAULT_SORT_COLUMN;
        const sortDir = ord && ord.dir === "asc" ? "ASC" : "DESC";
        const query = { ...filtersRef.current, sortColumn, sortDir, pageNo };
        const key = buildKey({ ...query, userId: userIdRef.current });

        const respond = (rows, total) =>
          callback({
            draw: params.draw,
            recordsTotal: total,
            recordsFiltered: total,
            data: rows,
          });

        const cached = readPage(key);
        if (cached) {
          respond(cached.rows, cached.total);
          return;
        }

        getOrderHistoryRef
          .current({ ...filtersRef.current, pageno: pageNo, sortColumn, sortDir })
          .then((res) => {
            const parsed = typeof res === "string" ? JSON.parse(res) : res;
            const rows = Array.isArray(parsed?.rows) ? parsed.rows : [];
            const total = Number(parsed?.total) || 0;
            writePage(key, { rows, total });
            respond(rows, total);
          })
          .catch(() => respond([], 0));
      },
      columns: [
        { data: "custOrderNo", defaultContent: "" },
        { data: "invoiceNo", render: (v) => `<a>${v ?? ""}</a>` },
        { data: "orderId", defaultContent: "" },
        {
          data: "createdDate",
          render: (v) => `<p class="order-date">${v ?? ""}</p>`,
        },
        {
          data: "orderDate",
          render: (v) => `<p class="order-date">${v ?? ""}</p>`,
        },
        {
          data: "orderStatus",
          render: (value, type, row) => {
            let statusClass = "default-order";
            for (const [cssClass, ids] of Object.entries(statusClassMap)) {
              if (ids.includes(row.orderStatusId)) {
                statusClass = cssClass;
                break;
              }
            }
            return `<p class="order-list-type ${statusClass}">${value ?? ""}</p>`;
          },
        },
        {
          data: "shipTrackUrl",
          render: (v, type, row) => {
            if (v)
              return `<div class="orders-button button-effect "><button
                  type="button"
                  class="js-track-order theme-primary"
                  style="width:100px;"
                  data-order-ref="${row.shipTrackUrl}"
                  data-track-url="${v}" >Track Order
                </button></div>`;
            else return ``;
          },
        },
        {
          data: null,
          orderable: false,
          render: (value, type, row) =>
            `<div class="orders-button">
              <button
                class="btn order-list-view js-view-order"
                data-order-id="${row.url_ref}">
                <span class="wes-eye"></span> View
              </button>
              <div class="button-effect pendingOrder-trash">
              ${
                invoiceEligible.includes(row.orderStatusId)
                  ? `<button
                  type="button"
                  class="btn order-pdf-download theme-primary js-download-invoice"
                  data-order-id="${row.orderId}"
                  data-invoice-no="${row.invoiceNo}">
                  <span class="wes-download"></span> Invoice
                </button>`
                  : paymentRequiredStatusIds.includes(row.orderStatusId)
                    ? `<button
                  type="button"
                  class="btn pay-now-green js-pay-now"
                  data-order-id="${row.url_ref}"
                  data-invoice-no="${row.invoiceNo}" >
                  Pay Now
                </button>`
                    : ``
              }
              </div>
             </div>`,
        },
      ],
      language: {
        paginate: {
          previous: '<i class="fa fa-chevron-left"></i>',
          next: '<i class="fa fa-chevron-right"></i>',
        },
        processing: '<div class="loader-container orderlist-loading"><div class="loader"></div></div>',
      },
      order: [[3, "desc"]],
      pageLength: 20,
      responsive: { details: { type: "column", target: "tr" } },
      autoWidth: false,
      searching: false,
      columnDefs: [
        { targets: [5, 6], orderable: false },
        { targets: [2, 3], className: "orderListing", responsivePriority: 3 },
      ],
      drawCallback: () => markFirstVisible(),
    });
    $table.on("responsive-resize.dt", () => markFirstVisible());
    markFirstVisible();
    $table.on("click", ".js-view-order", function () {
      const orderId = $(this).data("order-id");
      if (handleShow) {
        navigate(`/myaccount/order-history/view/${orderId}`);
      } else navigate(`/myaccount/order-history/view/${orderId}`);
    });

    $table.on("click", ".js-download-invoice", async function () {
      const $button = $(this);
      const oid = $button.data("order-id");
      const incNo = $button.data("invoice-no");

      // Store original state
      const originalHTML = $button.html();
      const originalBorder = $button.css("border");

      // Disable button and add loader
      $button.prop("disabled", true);
      $button.css("border", "none");
      $button.html('<i class="fas fa-spinner fa-spin"></i> Loading...');

      try {
        await sendInvoiceSignal(oid, incNo);
      } catch (error) {
        console.error("Invoice download error:", error);
      } finally {
        // Restore original state
        $button.html(originalHTML);
        $button.css("border", originalBorder);
        $button.prop("disabled", false);
      }
    });

    $table.on("click", ".js-track-order", async function () {
      const $button = $(this);
      const oid = $button.data("order-ref");
      if (oid && oid.includes("http")) {
        window.open(oid, "_blank");
        return;
      }
      navigate(`/myaccount/order-history/TrackOrder/${oid}`);
    });
    $table.on("click", ".js-pay-now", async function () {
      const $button = $(this);
      const oid = $button.data("order-id");
      navigate(`/myaccount/order-history/Payonline/${oid}`);
      // const incNo = $button.data("invoice-no");

      // // Store original state
      // const originalHTML = $button.html();
      // const originalBorder = $button.css("border");

      // // Disable button and add loader
      // $button.prop("disabled", true);
      // $button.css("border", "none");
      // $button.html('<i class="fas fa-spinner fa-spin"></i> Loading...');

      // try {
      //   await sendInvoiceSignal(oid, incNo);
      // } catch (error) {
      //   console.error("Invoice download error:", error);
      // } finally {
      //   // Restore original state
      //   $button.html(originalHTML);
      //   $button.css("border", originalBorder);
      //   $button.prop("disabled", false);
      // }
    });
    return () => {
      dtRef.current.destroy(true);
      dtRef.current = null;
    };
  }, []);

  return (
    <table ref={tableRef} className="table table-striped display nowrap orderlist-table" style={{ width: "100%" }}>
      <thead>
        <tr>
          <th style={{ width: "17% !important", paddingRight: "10px" }}>Cust.Order No</th>
          <th style={{ width: "15% !important", paddingRight: "10px" }}>Invoice Number</th>
          <th style={{ width: "17% !important", paddingRight: "10px" }}>Order Id</th>
          <th style={{ width: "17% !important", paddingRight: "10px" }}>Created Date</th>
          <th style={{ width: "17% !important", paddingRight: "10px" }}>Invoice Date</th>
          <th style={{ width: "17% !important", paddingRight: "10px" }}>Order Status</th>
          <th style={{ width: "20% !important", paddingRight: "10px" }}>Shipping &amp; Tracking</th>
          <th></th>
        </tr>
      </thead>
      <tbody />
    </table>
  );
};
